import { expandArcs } from './arcWalls.jsx';

const CELL_SIZE = 50; // اندازه هر سلول گرید تشخیص، ۱۰ سانتی‌متر
const PIXEL_TO_METER = 0.01; // مقیاس پروژه: هر ۱ پیکسل = ۱ سانتی‌متر واقعی

function pointToSegmentDistance(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSq = dx * dx + dy * dy;
  let t = lengthSq === 0 ? 0 : ((px - x1) * dx + (py - y1) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));
  const closestX = x1 + t * dx;
  const closestY = y1 + t * dy;
  return Math.sqrt((px - closestX) ** 2 + (py - closestY) ** 2);
}

function isCellBlocked(cx, cy, walls, threshold) {
  for (const wall of walls) {
    if (pointToSegmentDistance(cx, cy, wall.x1, wall.y1, wall.x2, wall.y2) <= threshold) {
      return true;
    }
  }
  return false;
}

// تشخیص خودکار فضای بسته از یک نقطه کلیک، با پرکردن سیل‌آسا روی گرید
export function detectRoom(wallsIn, clickX, clickY, wallThickness = 8) {
  const walls = expandArcs(wallsIn); // دیوارِ منحنی → تکه‌های صاف
  if (walls.length === 0) return { success: false, reason: 'noWalls' };

  const margin = 300;
  const xs = walls.flatMap((w) => [w.x1, w.x2]);
  const ys = walls.flatMap((w) => [w.y1, w.y2]);
  const minX = Math.min(...xs) - margin;
  const maxX = Math.max(...xs) + margin;
  const minY = Math.min(...ys) - margin;
  const maxY = Math.max(...ys) + margin;

  // بافر امنیتی نیم‌سلولی برای جلوگیری از نشتی از گوشه دیوارها
  const threshold = wallThickness / 2 + CELL_SIZE / 2;

  const cols = Math.ceil((maxX - minX) / CELL_SIZE);
  const rows = Math.ceil((maxY - minY) / CELL_SIZE);

  const startCol = Math.floor((clickX - minX) / CELL_SIZE);
  const startRow = Math.floor((clickY - minY) / CELL_SIZE);

  if (startCol < 0 || startCol >= cols || startRow < 0 || startRow >= rows) {
    return { success: false, reason: 'outOfBounds' };
  }

  const cellCenterX = (col) => minX + col * CELL_SIZE + CELL_SIZE / 2;
  const cellCenterY = (row) => minY + row * CELL_SIZE + CELL_SIZE / 2;

  if (isCellBlocked(cellCenterX(startCol), cellCenterY(startRow), walls, threshold)) {
    return { success: false, reason: 'onWall' };
  }

  const visited = new Set([`${startCol},${startRow}`]);
  const queue = [[startCol, startRow]];
  const blockedCache = new Map();
  let touchedBoundary = false;

  const checkBlocked = (col, row) => {
    const key = `${col},${row}`;
    if (blockedCache.has(key)) return blockedCache.get(key);
    const blocked = isCellBlocked(cellCenterX(col), cellCenterY(row), walls, threshold);
    blockedCache.set(key, blocked);
    return blocked;
  };

  while (queue.length > 0) {
    const [col, row] = queue.pop();
    if (col <= 0 || col >= cols - 1 || row <= 0 || row >= rows - 1) touchedBoundary = true;

    const neighbors = [[col + 1, row], [col - 1, row], [col, row + 1], [col, row - 1]];
    for (const [ncol, nrow] of neighbors) {
      if (ncol < 0 || ncol >= cols || nrow < 0 || nrow >= rows) {
        touchedBoundary = true;
        continue;
      }
      const key = `${ncol},${nrow}`;
      if (visited.has(key) || checkBlocked(ncol, nrow)) continue;
      visited.add(key);
      queue.push([ncol, nrow]);
    }

    if (visited.size > 200000) return { success: false, reason: 'tooLarge' };
  }

  if (touchedBoundary) return { success: false, reason: 'notEnclosed' };

  // تبدیل سلول‌های پرشده به ردیف‌های افقی فشرده، برای رندر بهینه (به‌جای هزاران مربع کوچک)
  const rowMap = new Map();
  let sumX = 0;
  let sumY = 0;

  visited.forEach((key) => {
    const [col, row] = key.split(',').map(Number);
    if (!rowMap.has(row)) rowMap.set(row, []);
    rowMap.get(row).push(col);
    sumX += cellCenterX(col);
    sumY += cellCenterY(row);
  });

  const rowSpans = [];
  rowMap.forEach((colsInRow, rowIndex) => {
    colsInRow.sort((a, b) => a - b);
    let spanStart = colsInRow[0];
    let prev = colsInRow[0];
    for (let i = 1; i <= colsInRow.length; i++) {
      const current = colsInRow[i];
      if (current !== prev + 1) {
        rowSpans.push({
          y: minY + rowIndex * CELL_SIZE,
          x1: minX + spanStart * CELL_SIZE,
          x2: minX + (prev + 1) * CELL_SIZE,
        });
        spanStart = current;
      }
      prev = current;
    }
  });

  const areaPx = visited.size * CELL_SIZE * CELL_SIZE;
  const areaSqm = areaPx * PIXEL_TO_METER * PIXEL_TO_METER;

  return {
    success: true,
    rowSpans,
    cellSize: CELL_SIZE,
    areaSqm,
    centroid: { x: sumX / visited.size, y: sumY / visited.size },
  };
}

// تبدیل مختصات داخلی صحنه (Konva) به مختصات صفحه، برای جاگذاری باکس نام روی HTML
export function worldToScreen(stage, point, toolbarHeight = 52) {
  const abs = stage.getAbsoluteTransform().point(point);
  return { x: abs.x, y: abs.y + toolbarHeight };
}